/**
 * pages/knowledge/my.js — «Миний минутын мэдлэг» (ажилтны хувийн хуудас)
 *
 * /me хуудсаар дамжиж орсон ажилтан өөрийн:
 *   • ХЯНАЛТ  — үзсэн хичээл, бөглөсөн тест, зөв хариултын хувь, Safety Point
 *   • ШҮҮЛТ   — хугацаа, групп, төлөвөөр
 *   • ЖАГСААЛТ — өдөр бүрийн бичлэг/тестийн дэлгэрэнгүй
 * харна.
 *
 * `KnowledgeHistoryPanel` нь /me хуудасны «М-Мэдлэг түүх» табад мөн ордог.
 *
 * Эх сурвалж: «process flow HSD-2026.vsdx» → "м-мэдлэг" таб, Гарц эгнээ
 * (үзсэн / тэнцсэн / оноо авсан ажилтны үзүүлэлт).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MonthTag from "src/components/knowledge/MonthTag";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  ConfigProvider,
  Col,
  DatePicker,
  Empty,
  Progress,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  message,
} from "antd";
import ResponsiveTable, { useIsNarrow } from "src/components/ResponsiveTable";
import moment from "moment";
import { knowledgeApi } from "src/api/knowledgeApi";
import { TodayPanel } from "src/pages/knowledge/register";
import { KNOWLEDGE, KNOWLEDGE_DARK } from "src/pages/knowledge/theme";
import useGoBack from "src/hooks/useGoBack";

const { RangePicker } = DatePicker;

const WARN = "#EA580C";

const STATUS_OPTIONS = [
  { value: "all", label: "Бүгд" },
  { value: "viewed", label: "Үзсэн" },
  { value: "answered", label: "Тест бөглөсөн" },
  { value: "correct", label: "Зөв хариулсан" },
  { value: "wrong", label: "Буруу хариулсан" },
  { value: "missed", label: "Үзсэн ч тест бөглөөгүй" },
];

/**
 * KPI карт.
 *
 * `compact` — /me хуудасны таб дотор ашиглах ЖИЖИГ хувилбар. Тэнд энэ хэсэг
 * нь өөрөө хуудсын гол агуулга биш, аль хэдийн нягт хуудасны нэг таб тул
 * бүтэн хэмжээгээрээ дэлгэцийн хэт их зайг эзэлдэг байв.
 * `/knowledge/my` бие даасан хуудсанд ХЭВЭЭР — тэнд KPI нь гол агуулга.
 */
function KpiCard({ label, value, sub, icon, color, compact }) {
  const c = compact
    ? { pad: 9, radius: 10, label: 10, icon: 13, value: 19, sub: 11, gap: 3 }
    : { pad: 14, radius: 12, label: 11, icon: 16, value: 26, sub: 12, gap: 6 };
  return (
    <Card bodyStyle={{ padding: c.pad }} style={{ borderRadius: c.radius }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span
          style={{
            fontSize: c.label,
            fontWeight: 700,
            letterSpacing: 0.4,
            color: "#8A94A6",
            textTransform: "uppercase",
            // Жижиг горимд урт шошго 2 мөр болж картын өндөр жигд бус
            // болохоос сэргийлнэ.
            whiteSpace: compact ? "nowrap" : "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
        <i className={"fas " + icon} style={{ color, fontSize: c.icon }} />
      </div>
      <p
        style={{
          fontSize: c.value,
          fontWeight: 800,
          margin: `${c.gap}px 0 0`,
          color: "#1F2430",
          lineHeight: 1.15,
        }}
      >
        {value}
      </p>
      {sub ? (
        <span style={{ fontSize: c.sub, color: "#8A94A6" }}>{sub}</span>
      ) : null}
    </Card>
  );
}

/**
 * Ажилтны өөрийн түүх — KPI хяналт + шүүлт + жагсаалт.
 * Бие даан ашиглагдана: /knowledge/my ба /me → «М-Мэдлэг түүх» таб.
 */
export function KnowledgeHistoryPanel({ compact = false } = {}) {
  // Утсан дээр шүүлтийн талбарууд БҮТЭН өргөн, тус бүр өөрийн мөрөнд —
  // 220px-ийн тогтмол өргөн нь нарийн дэлгэцэнд хоёр мөр болж хугарна.
  const narrow = useIsNarrow();
  const fw = narrow ? { width: "100%" } : null;

  // Анхдагч хугацаа — ТУХАЙН САР
  const [range, setRange] = useState([
    moment().startOf("month"),
    moment().endOf("month"),
  ]);
  const [groupId, setGroupId] = useState(null);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await knowledgeApi.myHistory({
          from: range?.[0]?.format("YYYY-MM-DD"),
          to: range?.[1]?.format("YYYY-MM-DD"),
          group_id: groupId || undefined,
          status,
        })
      );
    } catch (e) {
      message.error(e?.response?.data?.message || "Мэдээллийг татаж чадсангүй.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, groupId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;
  // ── Сэдвийн ГРУППЭЭР бүлэглэсэн жагсаалт ─────────────────────────────
  // Групп дотор огноогоор (шинэ нь дээр). Группгүй мөр эцэст.
  // Хуудаслалттай тул rowSpan-ыг ТУХАЙН ХУУДСАН дахь байрлалаар тооцно —
  // бүтэн жагсаалтаар тооцвол 2-р хуудсан дээр нүд нэгтгэл тасарна.
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [data]);

  const rows = useMemo(() => {
    const list = [...(data?.rows || [])];
    list.sort((a, b) => {
      const ga = a.group_id ?? "";
      const gb = b.group_id ?? "";
      if (String(ga) !== String(gb)) {
        if (!ga) return 1;
        if (!gb) return -1;
        return String(a.group_name || "").localeCompare(String(b.group_name || ""));
      }
      return String(b.date || "").localeCompare(String(a.date || ""));
    });
    return list;
  }, [data]);

  const spans = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const slice = rows.slice(start, start + PAGE_SIZE);
    const out = new Array(slice.length).fill(0);
    let i = 0;
    while (i < slice.length) {
      const k = String(slice[i].group_id ?? "");
      let n = 1;
      while (i + n < slice.length && String(slice[i + n].group_id ?? "") === k) n += 1;
      out[i] = n; // үлдсэн нь 0 — antd нүдийг огт зурахгүй
      i += n;
    }
    return out;
  }, [rows, page]);

  return (
    <>
      {/* Хяналтын хэсэг — өөрийн үзүүлэлт */}
      <Row
        gutter={compact ? [8, 8] : [12, 12]}
        style={{ marginBottom: compact ? 8 : 14 }}
      >
        <Col xs={12} md={6}>
          <KpiCard
            compact={compact}
            label="Үзсэн хичээл"
            value={s?.view_count || 0}
            sub={`${s?.day_count || 0} өдөр`}
            icon="fa-play-circle"
            color={KNOWLEDGE}
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            compact={compact}
            label="Тест бөглөсөн"
            value={s?.answer_count || 0}
            sub="асуулт"
            icon="fa-clipboard-check"
            color={KNOWLEDGE_DARK}
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            compact={compact}
            label="Зөв хариулт"
            value={`${s?.correct_percent || 0}%`}
            sub={`${s?.correct_count || 0} / ${s?.answer_count || 0}`}
            icon="fa-check-double"
            color="#16A34A"
          />
        </Col>
        <Col xs={12} md={6}>
          <KpiCard
            compact={compact}
            label="Safety Points"
            value={s?.points || 0}
            sub="Нийт цуглуулсан"
            icon="fa-award"
            color={WARN}
          />
        </Col>
      </Row>

      {s?.answer_count ? (
        <Card
          bodyStyle={{ padding: compact ? 9 : 14 }}
          style={{ marginBottom: compact ? 8 : 14 }}
        >
          <div
            style={{
              fontSize: compact ? 11 : 12,
              fontWeight: 700,
              marginBottom: compact ? 2 : 6,
            }}
          >
            Мэдлэгийн бататгал (зөв хариултын хувь)
          </div>
          <Progress
            size={compact ? "small" : "default"}
            percent={s?.correct_percent || 0}
            strokeColor={
              (s?.correct_percent || 0) >= 80
                ? "#16A34A"
                : (s?.correct_percent || 0) >= 50
                ? WARN
                : "#DC2626"
            }
          />
        </Card>
      ) : null}

      {/* Шүүлтийн хэсэг */}
      <Card bodyStyle={{ padding: 8 }} style={{ marginBottom: 10 }}>
        <ConfigProvider componentSize="small">
          {/* Шошгогүй — талбар бүр өөрөө placeholder-оор ойлгогдоно; утсан
              дээр «Хугацаа: … Групп: …» гэсэн үгс мөр эзэлж, шүүлт хоёр
              мөр болж хугардаг байв. */}
          <Space
            wrap
            size={6}
            direction={narrow ? "vertical" : "horizontal"}
            style={narrow ? { width: "100%" } : undefined}
          >
          <RangePicker
            value={range}
            onChange={setRange}
            format="YYYY-MM-DD"
            allowClear={false}
            placeholder={["Эхлэх", "Дуусах"]}
            style={fw || undefined}
          />
          <Select
            allowClear
            style={fw || { width: 220 }}
            placeholder="Бүх групп"
            value={groupId}
            onChange={setGroupId}
            options={(data?.groups || []).map((g) => ({
              value: g.id,
              label: g.name,
            }))}
          />
          <Select
            style={fw || { width: 210 }}
            placeholder="Төлөв"
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
          />
          <Button
            onClick={load}
            loading={loading}
            title="Шинэчлэх"
            aria-label="Шинэчлэх"
            icon={<i className="fas fa-sync" />}
            block={narrow}
          />
        </Space>
        </ConfigProvider>
      </Card>

      {/* Жагсаалт */}
      <Card bodyStyle={{ padding: 0 }}>
        <ResponsiveTable
          rowKey={(r) => `${r.date}-${r.topic_id}`}
          size="small"
          loading={loading}
          dataSource={rows}
          locale={{
            emptyText: (
              <Empty
                description="Энэ хугацаанд бүртгэл алга"
                style={{ padding: 30 }}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
            onChange: setPage,
          }}
          columns={[
            {
              title: "№",
              width: 46,
              align: "center",
              // Хуудас дамнасан дугаар — 2-р хуудас 21-ээс эхэлнэ
              render: (_t, _r, i) => (
                <span style={{ color: "#8A94A6", fontSize: 12 }}>
                  {(page - 1) * PAGE_SIZE + i + 1}
                </span>
              ),
            },
            {
              title: "Сэдвийн групп",
              dataIndex: "group_name",
              width: 180,
              /* ⚠ САР нь бүлгийн НЭР дээр гарна — «аль сарын сэдэв вэ»
                 гэдэг нь жагсаалтаас шууд харагдах ёстой (2026-09-30). */
              render: (v, r) => (
                <div style={{ lineHeight: 1.35 }}>
                  <div>{v}</div>
                  <MonthTag month={r.group_month} style={{ marginTop: 2 }} />
                </div>
              ),
              // Нэг группын мөрүүдийг нэг нүд болгон нэгтгэнэ (тухайн хуудсанд)
              onCell: (_r, i) => ({
                rowSpan: spans[i] ?? 1,
                style: { background: "#FAFBFD", verticalAlign: "top" },
              }),
              render: (v, r) =>
                v ? (
                  <Space>
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 5,
                        background: r.group_color || KNOWLEDGE,
                        display: "inline-block",
                      }}
                    />
                    <b>{v}</b>
                  </Space>
                ) : (
                  <span style={{ color: "#8A94A6" }}>Группгүй</span>
                ),
            },
            {
              title: "Сэдэв",
              dataIndex: "topic_name",
              // Огноо нь сэдвийн ДООР — тусдаа багана байхаа болив
              render: (v, r) => (
                <Space direction="vertical" size={0}>
                  <span>{v || "-"}</span>
                  <span style={{ fontSize: 11, color: "#8A94A6" }}>{r.date}</span>
                </Space>
              ),
            },
            {
              title: "Хичээл",
              dataIndex: "content_title",
              render: (v, r) => (
                <Space direction="vertical" size={0}>
                  <span>{v || "-"}</span>
                  {/* Үзсэн эсэх нь хичээлийн ДООР — тусдаа багана байхаа болив */}
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: r.viewed ? "#16A34A" : "#DC2626",
                    }}
                  >
                    {r.viewed ? "✓ Үзсэн" : "✗ Үзээгүй"}
                  </span>
                  {r.question_text ? (
                    <span style={{ fontSize: 11, color: "#8A94A6" }}>
                      {r.question_text.length > 60
                        ? r.question_text.slice(0, 60) + "…"
                        : r.question_text}
                    </span>
                  ) : null}
                </Space>
              ),
            },
            {
              title: "Тест",
              width: 130,
              align: "center",
              render: (_, r) =>
                !r.answered ? (
                  <Tag>Бөглөөгүй</Tag>
                ) : r.is_correct ? (
                  <Tag color="green">{r.answer_option} · Зөв</Tag>
                ) : (
                  <Tag color="red">{r.answer_option} · Буруу</Tag>
                ),
            },
            {
              title: "Оноо",
              dataIndex: "points",
              width: 80,
              align: "center",
              render: (v) =>
                v ? <b style={{ color: KNOWLEDGE_DARK }}>+{v}</b> : <span>0</span>,
            },
          ]}
        />
      </Card>
    </>
  );
}

const KnowledgeMy = () => {
  const navigate = useNavigate();
  // Буцах — ирсэн хуудас руу (state.from → түүх → анхдагч)
  const goBack = useGoBack("/me");
  const [tab, setTab] = useState("today");
  const [todayKey, setTodayKey] = useState(0);

  return (
    <div style={{ padding: 16 }}>
      {/* Толгой — хяналт шалгалтын хуудастай ИЖИЛ banner загвар.
          ⚠ Энд ModuleHeader компонент ашиглахгүй, зориуд ШУУД бичсэн:
          гадаад import нэмэхэд dev server кэшийн улмаас «is not defined»
          алдаа удаан үлддэг байсан. Загвар нь бусад хуудастай ижил. */}
      <div
        style={{
          background: "linear-gradient(135deg, #1D5673 0%, #2C7EA1 100%)",
          borderRadius: 14,
          padding: "16px 18px",
          marginBottom: 14,
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <i className="fas fa-user-graduate" style={{ fontSize: 24 }} />
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 500,
                letterSpacing: 0.3,
                color: "rgba(255,255,255,0.88)",
              }}
            >
              Миний минутын мэдлэг
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.85 }}>
              Үзсэн хичээл, бөглөсөн тест, цуглуулсан Safety Point
            </p>
          </div>
        </div>
        <Button
          style={{
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.55)",
            borderRadius: 10,
            fontWeight: 700,
          }}
          onClick={() => goBack()}
        >
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />
          Буцах
        </Button>
      </div>

      {/* Ажилтанд ЗӨВХӨН энэ хоёр таб — мэдлэгийн сан, 7 хоногийн хуваарь нь
          ХАБЭА хэлтсийн /knowledge/register хуудсанд үлдэнэ. */}
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          setTab(k);
          if (k === "today") setTodayKey((n) => n + 1);
        }}
        items={[
          {
            key: "today",
            label: "Өнөөдрийн мэдлэг",
            children: <TodayPanel refreshKey={todayKey} />,
          },
          {
            key: "history",
            label: "Миний түүх",
            children: <KnowledgeHistoryPanel />,
          },
        ]}
      />
    </div>
  );
};

export default KnowledgeMy;
